
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Flame } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ConfettiEffect } from './ConfettiEffect';
import type { Milestone } from '@/hooks/useMilestoneCelebration';

interface MilestoneCelebrationDialogProps {
  open: boolean;
  onClose: () => void;
  milestone: Milestone | null;
}

const getIcon = (type: Milestone['type']) => {
  switch (type) {
    case 'matches':
      return <Trophy className="h-12 w-12 text-yellow-500" />;
    case 'streak':
      return <Flame className="h-12 w-12 text-orange-500" />;
    case 'rating':
      return <Star className="h-12 w-12 text-primary" />;
    default:
      return <Trophy className="h-12 w-12 text-yellow-500" />;
  }
};

const getBackgroundGradient = (type: Milestone['type']) => {
  switch (type) {
    case 'matches':
      return 'from-yellow-500/20 via-amber-500/10 to-transparent';
    case 'streak':
      return 'from-orange-500/20 via-red-500/10 to-transparent';
    case 'rating':
      return 'from-primary/20 via-green-500/10 to-transparent';
    default:
      return 'from-primary/20 via-transparent to-transparent';
  }
};

export const MilestoneCelebrationDialog: React.FC<MilestoneCelebrationDialogProps> = ({
  open,
  onClose,
  milestone,
}) => {
  if (!milestone) return null;

  return (
    <>
      <ConfettiEffect isActive={open} duration={4000} />
      
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="sm:max-w-md border-0 bg-transparent shadow-none">
          <AnimatePresence mode="wait">
            {open && (
              <motion.div
                initial={{ scale: 0.8, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.8, opacity: 0, y: 20 }}
                transition={{ type: 'spring', duration: 0.5 }}
                className="relative overflow-hidden rounded-2xl bg-card border border-border shadow-2xl"
              >
                {/* Background gradient */}
                <div className={`absolute inset-0 bg-gradient-to-b ${getBackgroundGradient(milestone.type)}`} />
                



                <div className="relative p-8 flex flex-col items-center text-center space-y-6">
                  {/* Animated icon */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ 
                      type: 'spring', 
                      delay: 0.2, 
                      stiffness: 200,
                      damping: 15 
                    }}
                    className="relative"
                  >
                    {/* Glow effect */}
                    <div className="absolute inset-0 blur-xl opacity-50">
                      {getIcon(milestone.type)}
                    </div>
                    {getIcon(milestone.type)}
                  </motion.div>

                  {/* Emoji */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', delay: 0.3 }}
                    className="text-5xl"
                  >
                    {milestone.icon}
                  </motion.div>

                  {/* Title */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <h2 className="text-2xl font-bold text-foreground mb-2">
                      Congratulations!
                    </h2>
                    <h3 className="text-xl font-semibold text-primary">
                      {milestone.title}
                    </h3>
                  </motion.div>

                  {/* Description */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-muted-foreground text-sm max-w-xs space-y-1"
                  >
                    <p>{milestone.description}</p>
                    {typeof milestone.currentTotal === 'number' && (
                      <p className="text-xs">
                        {milestone.type === 'matches' && (
                          <>You are now at {milestone.currentTotal} competitive matches. Tournament matches are counted separately.</>
                        )}
                        {milestone.type === 'streak' && <>Current streak: {milestone.currentTotal} wins.</>}
                        {milestone.type === 'rating' && <>Current rating: {milestone.currentTotal}.</>}
                      </p>
                    )}
                  </motion.div>


                  {/* Badge */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', delay: 0.6, stiffness: 300 }}
                    className="px-4 py-2 rounded-full bg-primary/10 border border-primary/20"
                  >
                    <span className="text-sm font-medium text-primary">
                      {milestone.type === 'matches' && '🎾 Match Milestone'}
                      {milestone.type === 'streak' && '🔥 Streak Achievement'}
                      {milestone.type === 'rating' && '⭐ Rating Milestone'}
                    </span>
                  </motion.div>

                  {/* Action button */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="pt-2"
                  >
                    <Button onClick={onClose} className="px-8">
                      Continue Playing
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </>
  );
};
